import { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useLanguage } from "../contexts/LanguageContext";
import Sidebar from "../components/Sidebar";

// SVG Icon Components
const ShieldIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
 strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
    />
  </svg>
);

const LockIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
 strokeLinejoin="round"
      strokeWidth={2}
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
    />
  </svg>
);

const AlertIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
 strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
    />
  </svg>
);

const UsersIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
 strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);

interface SecurityMetrics {
  systemHealth: number;
  totalRevenue: number;
  totalPayments: number;
  activeUsers: number;
}

interface WorkerStatus {
  name: string;
  status: "idle" | "running" | "completed" | "failed";
  lastRun: Date;
  duration: string;
}

export default function Security() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [metrics, setMetrics] = useState<SecurityMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [workers, setWorkers] = useState<WorkerStatus[]>([
    { name: "Report Generator", status: "idle", lastRun: new Date(), duration: "0s" },
    { name: "Data Cleanup", status: "idle", lastRun: new Date(), duration: "0s" },
    { name: "Analytics Processing", status: "idle", lastRun: new Date(), duration: "0s" },
    { name: "Notification Worker", status: "idle", lastRun: new Date(), duration: "0s" },
    { name: "CSV Auto Sync", status: "idle", lastRun: new Date(), duration: "0s" },
  ]);
  const [triggeringWorker, setTriggeringWorker] = useState<string | null>(null);

  useEffect(() => {
    // Mock security metrics
    const securityMetrics: SecurityMetrics = {
      systemHealth: 86,
      totalRevenue: 1234567,
      totalPayments: 4567,
      activeUsers: 247
    };
    setMetrics(securityMetrics);
    setLoading(false);

    // Update clock
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const triggerWorker = async (workerName: string) => {
    setTriggeringWorker(workerName);
    setWorkers(prev => prev.map(w => 
      w.name === workerName 
        ? { ...w, status: "running" }
        : w
    ));

    try {
      const actionMap: Record<string, string> = {
        "Report Generator": "generate_report",
        "Data Cleanup": "cleanup_data",
        "Analytics Processing": "process_analytics",
        "Notification Worker": "send_notification",
        "CSV Auto Sync": "sync_csv"
      };

      const response = await fetch('https://super-prompt-optimizer.sv9.workers.dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: actionMap[workerName] || 'optimize',
          worker: workerName,
          timestamp: new Date().toISOString()
        }),
      });

      if (response.ok) {
        setWorkers(prev => prev.map(w => 
          w.name === workerName 
            ? { ...w, status: "completed", lastRun: new Date(), duration: "2.3s" }
            : w
        ));
      } else {
        setWorkers(prev => prev.map(w => 
          w.name === workerName 
            ? { ...w, status: "failed" }
            : w
        ));
      }
    } catch (error) {
      console.error('Worker trigger error:', error);
      setWorkers(prev => prev.map(w => 
        w.name === workerName 
          ? { ...w, status: "failed" }
          : w
      ));
    } finally {
      setTriggeringWorker(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading security dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar />
      <div className="flex-1">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Security Dashboard</h1>
              <p className="text-sm text-gray-500">System health and security metrics</p>
            </div>
            <div className="flex items-center gap-6">
              {/* Clock */}
              <div className="text-right">
                <div className="font-mono text-2xl font-bold text-gray-900">
                  {currentTime.toLocaleTimeString("en-GB", { hour12: false })}
                </div>
                <div className="text-sm text-gray-500">
                  {currentTime.toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Security Grid */}
        <div className="p-6">
          {/* Security Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="text-sm text-gray-600 font-medium">System Health</div>
              <div className="text-2xl font-bold text-gray-900 mt-2">{metrics?.systemHealth || 0}%</div>
              <div className="text-sm text-green-600 mt-1">Excellent</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="text-sm text-gray-600 font-medium">Total Revenue</div>
              <div className="text-2xl font-bold text-gray-900 mt-2">฿{metrics?.totalRevenue?.toLocaleString() || "0"}</div>
              <div className="text-sm text-green-600 mt-1">+12.5%</div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="text-sm text-gray-600 font-medium">Total Payments</div>
              <div className="text-2xl font-bold text-gray-900 mt-2">{metrics?.totalPayments?.toLocaleString() || "0"}</div>
              <div className="text-sm text-green-600 mt-1">+8.2%</div>
            </div>

            {/* Active Users */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600 font-medium">Active Users</span>
                <span className="text-gray-400">
                  <UsersIcon />
                </span>
              </div>
              <div className="text-4xl font-bold text-gray-900">
                {metrics?.activeUsers || 0}
              </div>
              <div className="text-sm text-gray-500 mt-2">Real-time</div>
            </div>
          </div>

          {/* Security Alerts */}
          <div className="card bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow mt-6">
            <h3 className="font-semibold text-gray-900 mb-4">Security Alerts</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 mt-2 bg-green-500 rounded-full"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    All security protocols active
                  </p>
                  <p className="text-xs text-gray-500">
                    2 minutes ago • System check completed
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 mt-2 bg-blue-500 rounded-full"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Firewall operating normally
                  </p>
                  <p className="text-xs text-gray-500">
                    15 minutes ago • No threats detected
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 mt-2 bg-yellow-500 rounded-full"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    SSL certificate valid
                  </p>
                  <p className="text-xs text-gray-500">
                    1 hour ago • Expires in 89 days
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Agentic Workers Management */}
          <div className="card bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow mt-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <LockIcon />
              Agentic Workers Management
            </h3>
            <p className="text-sm text-gray-600 mb-4">Trigger and monitor agentic workers for automated tasks</p>
            
            <div className="space-y-3">
              {workers.map((worker) => (
                <div
                  key={worker.name}
                  className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        worker.status === "running"
                          ? "bg-blue-500 animate-pulse"
                          : worker.status === "completed"
                            ? "bg-green-500"
                            : worker.status === "failed"
                              ? "bg-red-500"
                              : "bg-gray-400"
                      }`}
                    ></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{worker.name}</p>
                      <p className="text-xs text-gray-500">
                        Last run: {worker.lastRun.toLocaleString()} • Duration: {worker.duration}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => triggerWorker(worker.name)}
                    disabled={triggeringWorker === worker.name || worker.status === "running"}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {triggeringWorker === worker.name
                      ? "Running..."
                      : worker.status === "running"
                        ? "Running"
                        : "Trigger"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
